import React, { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, Plus, Save, Pencil, X } from "lucide-react";
import { api, imgUrl } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const AdminProducts = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", price: "" });
  const inputs = useRef({});
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    const res = await api.get("/admin/products", authHeader);
    setProducts(res.data.products);
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load products."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNew = () => { setEditing("new"); setForm({ name: "", description: "", price: "" }); };
  const startEdit = (p) => { setEditing(p.id); setForm({ name: p.name, description: p.description, price: String(p.price) }); };
  const cancel = () => { setEditing(null); setForm({ name: "", description: "", price: "" }); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required.");
    const price = parseFloat(form.price);
    if (isNaN(price) || price < 0) return toast.error("Enter a valid price.");
    setBusy(true);
    try {
      const payload = { name: form.name, description: form.description, price };
      if (editing === "new") {
        await api.post("/admin/products", payload, authHeader);
        toast.success("Product added!");
      } else {
        await api.put(`/admin/products/${editing}`, payload, authHeader);
        toast.success("Product updated!");
      }
      cancel();
      await load();
    } catch {
      toast.error("Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this product?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/products/${id}`, authHeader);
      toast.success("Product removed.");
      await load();
    } catch {
      toast.error("Could not remove product.");
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (id, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/products/${id}/image`, fd, authHeader);
      toast.success("Photo updated!");
      await load();
    } catch {
      toast.error("Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-2xl font-bold text-ink">Other Products</h3>
          <p className="text-sm text-ashydark">Sell items beyond cookies — drinks, merch, specials. These appear in an "Also Available" section.</p>
        </div>
        <Button data-testid="add-product-button" onClick={startNew} className="rounded-full bg-cocoa text-white hover:bg-cocoadark"><Plus className="mr-1.5 h-4 w-4" /> Add product</Button>
      </div>

      {editing === "new" && <ProductForm form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy} isNew />}

      {products.length === 0 && editing !== "new" && (
        <div className="rounded-xl border border-dashed border-ashy/40 bg-white py-12 text-center text-ashydark" data-testid="no-products">No extra products yet. Click "Add product" to create one.</div>
      )}

      <div className="space-y-4">
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className="rounded-xl border border-ashy/20 bg-white p-4">
            {editing === p.id ? (
              <ProductForm form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy} />
            ) : (
              <div className="flex items-start gap-4">
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-ashy/30 bg-cream">
                  {p.image && <img src={imgUrl(p.image)} alt={p.name} className="h-full w-full object-cover" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-ink">{p.name}</h4>
                    <span className="font-semibold text-cocoa">${Number(p.price).toFixed(2)}</span>
                  </div>
                  <p className="text-sm text-ashydark">{p.description}</p>
                  <div className="mt-2 flex gap-2">
                    <input type="file" accept="image/*" hidden ref={(el) => (inputs.current[p.id] = el)} data-testid={`product-file-input-${p.id}`} onChange={(e) => { uploadImage(p.id, e.target.files[0]); e.target.value = ""; }} />
                    <Button size="sm" variant="outline" data-testid={`product-photo-${p.id}`} onClick={() => inputs.current[p.id]?.click()} className="rounded-full border-ashy/40"><ImagePlus className="mr-1.5 h-4 w-4" /> Photo</Button>
                    <Button size="sm" variant="outline" data-testid={`edit-product-${p.id}`} onClick={() => startEdit(p)} className="rounded-full border-ashy/40"><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" data-testid={`delete-product-${p.id}`} onClick={() => remove(p.id)} className="rounded-full border-ashy/40 text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const ProductForm = ({ form, setForm, onSave, onCancel, busy, isNew }) => (
  <div className="rounded-lg border border-cocoa/30 bg-babybluelight/40 p-4" data-testid={isNew ? "new-product-form" : "edit-product-form"}>
    <div className="grid gap-3">
      <Input data-testid="product-name-input" placeholder="Product name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white" />
      <Textarea data-testid="product-desc-input" placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white" />
      <Input data-testid="product-price-input" type="number" step="0.01" placeholder="Price (e.g. 3.50)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-white" />
      <div className="flex gap-2">
        <Button data-testid="save-product-button" onClick={onSave} disabled={busy} className="rounded-full bg-cocoa text-white hover:bg-cocoadark"><Save className="mr-1.5 h-4 w-4" /> Save</Button>
        <Button variant="ghost" onClick={onCancel} className="rounded-full"><X className="mr-1.5 h-4 w-4" /> Cancel</Button>
      </div>
    </div>
  </div>
);
